import { useEffect, useRef, useState, type RefObject } from 'react';
import { PagerButtons } from './pager-buttons';
import { usePoll } from './use-poll';

export interface CardPage<T> {
  items: T[];
  nextCursor?: string;
}

/**
 * QA-69, QA-85: the cursor stack that every paged list shares (the cards on **Documents** and
 * **Test extraction**, and the tables on **Not processed**). `load` reads the page that a cursor
 * opens (`undefined` opens the first). `cursors` is the stack of the cursors that opened each
 * page, so `newer` pops one and `older` pushes the `nextCursor` of the page on the screen.
 *
 * - `version` changing sends the list back to its first page (a new upload).
 * - `poll` reloads the first page every few seconds. An older page never polls.
 * - `reload` reads the page on the screen again (after a write). It keeps `failed`, so a poll does
 *   not blank an error notice.
 * - `retry` (QA-101) clears `failed`, so the loading line shows, and reads again. After a good read,
 *   focus goes to `focusTarget`, because the **Try again** button has left the screen.
 */
export function usePagedList<T>(
  load: (cursor: string | undefined, signal: AbortSignal) => Promise<CardPage<T>>,
  version: unknown = 0,
  poll = false,
  focusTarget?: RefObject<HTMLElement | null>,
) {
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [page, setPage] = useState<CardPage<T> | null>(null);
  const [failed, setFailed] = useState(false);
  const [tick, setTick] = useState(0);
  const [seen, setSeen] = useState(version);
  const retried = useRef(false);
  const focusRef = useRef(focusTarget);
  focusRef.current = focusTarget;
  const loadRef = useRef(load);
  loadRef.current = load;
  const cursor = cursors[cursors.length - 1];
  const first = cursors.length === 1;
  const next = page?.nextCursor;

  // A new `version` drops the page before the next render, so the old cursor is never requested again.
  if (seen !== version) {
    setSeen(version);
    setCursors([undefined]);
    setPage(null);
  }

  useEffect(() => {
    const controller = new AbortController();
    loadRef.current(cursor, controller.signal).then(
      (result) => {
        setPage(result);
        setFailed(false);
        if (retried.current) focusRef.current?.current?.focus();
        retried.current = false;
      },
      () => {
        if (controller.signal.aborted) return;
        retried.current = false;
        setFailed(true);
      },
    );
    return () => controller.abort();
  }, [cursor, version, tick]);

  const reload = () => setTick((value) => value + 1);
  const retry = () => {
    retried.current = true;
    setFailed(false);
    reload();
  };
  usePoll(reload, poll && first);

  function go(stack: (string | undefined)[]) {
    setPage(null);
    setFailed(false);
    setCursors(stack);
  }

  return {
    page,
    failed,
    first,
    next,
    cursor,
    reload,
    retry,
    older: () => go([...cursors, next]),
    newer: () => go(cursors.slice(0, -1)),
  };
}

/**
 * **Newer** and **Older**, under a list (QA-99: a button shows only when it has somewhere to go).
 * While a page loads, `PagerButtons` stays mounted with no buttons, so it keeps its focus record.
 */
export function Pager({
  list,
  focusTarget,
}: {
  list: Pick<ReturnType<typeof usePagedList>, 'page' | 'first' | 'next' | 'older' | 'newer'>;
  focusTarget?: RefObject<HTMLElement | null>;
}) {
  const { page, first, next } = list;
  return (
    <PagerButtons
      busy={!page}
      canNewer={!first}
      canOlder={!!next}
      onNewer={list.newer}
      onOlder={list.older}
      focusTarget={focusTarget}
    />
  );
}
